// @ts-nocheck

// App
import React from 'react';
import { connect } from 'react-redux';
import { yieldCallback } from '@zillow/yield-callback';
import SafeDeferredRender, { RenderMode } from 'app/shared/modules/SafeDeferredRender';

// Actions
import {
  srwmCheckAndFetchNearbyListings,
  srwmFetchAreaDataByResourceId,
  srwmFetchFilterFromUrlAndApi,
  srwmFetchListingsAndOtherData,
} from 'app/shared/pages/SearchResultsPage/serverRouteWillMountFunctions';
import {
  csrCheckAndFetchNearbyListings,
  csrFetchAreaDataByResourceId,
  csrFetchFilterListingsAndSyncRecentSearch,
  csrFetchGeoLocation,
  csrFetchListingsAndHistogramData,
  csrFetchListingsAndSyncRecentSearch,
  csrFetchListingsOnly,
} from 'app/shared/pages/SearchResultsPage/clientSideFetchingFunctions';
import AreaActions from 'app/shared/flux/actions/AreaActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';

// Components
import AreaInformationWrapper from 'app/shared/modules/MobileSRP/AreaInformationWrapper';
import FilterBar from 'app/shared/modules/filter/FilterBar';
import Footer from 'app/shared/modules/Footer';
import List from 'app/shared/modules/MobileSRP/ListingCards/List';
import MapButton from 'app/shared/modules/MobileSRP/Map/MapButton';
import AreaHead from 'app/shared/modules/area/AreaHead';
import NextPageLink from 'app/shared/modules/MobileSRP/NextPageLink';
import NotFoundPage from 'app/shared/pages/NotFoundPage';
import SeoFooter from 'app/shared/modules/SeoFooter';
import FeedView from 'app/shared/modules/FeedView';
import FeedSkeletonView from 'app/shared/modules/FeedView/FeedSkeletonView';
import { useViewMode } from 'app/shared/hooks/useViewMode';
import { SavedListingsProvider } from 'app/shared/halo/contexts/SavedListingsContext';
import { BottomSheet } from 'app/shared/halo/components/ui/BottomSheet';
import { ShortlistContent } from 'app/shared/halo/components/features/listings/ShortlistContent';
import { closeShortlistSheet, useShortlistSheet } from 'app/shared/halo/stores/shortlistSheetStore';
import { selectShowHaloFeed, selectShowHaloShortlist } from 'app/shared/flux/selectors/haloExperimentSelectors';

// Tracking

// Misc / styles
import * as S from './styles';
import { listingUtils_getResourceIdAndSlug } from 'app/shared/utils/listingUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import loadGeolocation from 'app/shared/wrappers/FetchAreaWrapper/loadGeolocation';
import { emitTrackViewSrpEvent } from 'app/shared/models/Clickstream/PageViewEvents';
import { logError } from '@zg-rentals/log-error';
import { getNavigationAction } from 'app/shared/pages/SearchResultsPage/getNavigationAction';
import {
  getMountFetchStrategy,
  setLastFetchedDatasetKey,
} from 'app/shared/pages/SearchResultsPage/getMountFetchStrategy';
import { shouldSyncRecentSearchOnSrpMount } from 'app/shared/pages/SearchResultsPage/recentSearchSync';
import { restoreSrpListScroll } from 'app/shared/hooks/useSrpScrollRestore';

const MobileShortlistBottomSheet = () => {
  const { isOpen } = useShortlistSheet();
  return (
    <BottomSheet open={isOpen} onClose={closeShortlistSheet} title="My Shortlist">
      <ShortlistContent />
    </BottomSheet>
  );
};

const MobileListingView = ({ items, lazyLoadRef, handleInitializeMapClick, showHaloFeed, listingsLoading }) => {
  const [viewMode] = useViewMode();

  if (viewMode === 'feed' && showHaloFeed) {
    return listingsLoading || items.length === 0 ? <FeedSkeletonView /> : <FeedView listings={items} />;
  }

  return <List items={items} lazyLoadRef={lazyLoadRef} handleInitializeMapClick={handleInitializeMapClick} />;
};

class SearchResultsPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isMapInitialized: false,
    };

    // Create ref that will be used to trigger below the fold API calls
    this.lazyLoadRef = React.createRef();

    // Create Intersection Observer placeholder variable
    this.observer;
    this.scrollBottomObserver;
    this.recentSearchSyncKey = null;
  }

  /**
   * When component mounts, create the Intersection Observer instance that will be
   * in charge of lazy API fetching below the fold content; content will be loaded
   * when the user scrolls near the end of listings.
   */
  componentDidMount() {
    const { dispatch, pathname, query, areaResourceId, searchSlug, isClientSideLoadedPage, isInitialSsrPage } =
      this.props;

    const strategy = getMountFetchStrategy({
      pathname,
      query,
      areaResourceId,
      searchSlug,
      isClientSideLoadedPage,
      isInitialSsrPage,
    });

    if (strategy.type !== 'none') {
      // Mark that scroll restore should wait until fresh data arrives.
      // Without this, stale listing elements in the DOM cause premature
      // scroll restoration that gets overwritten by the re-render.
      this._deferScrollRestore = true;
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', false));

      if (strategy.type === 'listingsOnly') {
        this.handleListingsOnlyRefresh(strategy.pathname, strategy.query);
      } else {
        this.handleClientSideLoadedSrp(strategy.pathname, strategy.query);
      }
    } else {
      // HP-7456: Record dataset key for SSR/skip case so subsequent
      // back-navs can match against it.
      setLastFetchedDatasetKey(pathname, query);
      this.syncRecentSearchIfStale();
    }

    // Intersection Observer
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 1) {
          // Fetch below the fold content when the ref is fully in view
          this.fetchBelowTheFoldContent();

          // Once below the fold content is called, unobserve the ref to prevent superfluous calls
          this.observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 500px 0px',
        threshold: 1.0,
      },
    );

    this.scrollBottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 1) {
          // Once tracking is called, unobserve the ref to prevent superfluous calls
          this.scrollBottomObserver.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 50px 0px',
        threshold: 1.0,
      },
    );

    // Start observering the ref
    if (this.lazyLoadRef.current) {
      this.observer.observe(this.lazyLoadRef.current);
      this.scrollBottomObserver.observe(this.lazyLoadRef.current);
    }

    // Only restore scroll immediately if data is already loaded (SSR case).
    // When re-fetching, defer until componentDidUpdate sees fresh data.
    if (!this._deferScrollRestore) {
      const { cleanup } = restoreSrpListScroll({
        getScrollContainer: () => document.documentElement,
        offsetPx: 85,
      });
      this._scrollRestoreCleanup = cleanup;
    }

    emitTrackViewSrpEvent();
  }

  /**
   *
   * @param {Object} prevProps  - Previous props provided by React
   * Logic in the componentDidUpdate lifecycle method will watch for change in the app
   * and call functions to fetch for new listings.
   *
   * These changes could be:
   * 1) Area resource id has changed (searching new location)
   * 2) Search slug has changed (searching for different listing types)
   * 3) Query parameters have changed (beds, orderBy, pets, lat, lon, z, etc)
   */
  componentDidUpdate(prevProps) {
    const { dispatch, pathname, query } = this.props;
    const action = getNavigationAction(prevProps, this.props);

    // HP-7456: Update dataset key when listings finish loading so the next
    // back-nav can match against the current URL identity. Skip if the fetch
    // failed — error catch blocks force fetchListingsByCoordsComplete back to
    // true for UI recovery, but the data is stale so we must not record it.
    if (
      !prevProps.fetchListingsByCoordsComplete &&
      this.props.fetchListingsByCoordsComplete &&
      !this._lastFetchFailed
    ) {
      setLastFetchedDatasetKey(this.props.pathname, this.props.query);
    }
    this._lastFetchFailed = false;

    // Restore scroll after deferred re-fetch completes (list view).
    // FeedView handles its own restore via useSrpFeedScrollRestore.
    if (
      this._deferScrollRestore &&
      !prevProps.fetchListingsByCoordsComplete &&
      this.props.fetchListingsByCoordsComplete
    ) {
      this._deferScrollRestore = false;
      const { cleanup } = restoreSrpListScroll({
        getScrollContainer: () => document.documentElement,
        offsetPx: 85,
      });
      this._scrollRestoreCleanup = cleanup;
    }

    // Only sync when the navigation action doesn't already call updateRecentSearch.
    // areaResourceIdChanged, hamburgerSlugSwitch, and searchSlugChanged all sync
    // recent search as part of their data-fetching pipeline via
    // csrFetchFilterListingsAndSyncRecentSearch.
    const actionAlreadySyncs =
      action.type === 'areaResourceIdChanged' ||
      action.type === 'hamburgerSlugSwitch' ||
      action.type === 'searchSlugChanged';

    if (
      !actionAlreadySyncs &&
      (prevProps.recentSearch !== this.props.recentSearch ||
        prevProps.filter !== this.props.filter ||
        prevProps.pathname !== this.props.pathname)
    ) {
      this.syncRecentSearchIfStale();
    }

    switch (action.type) {
      case 'areaResourceIdChanged':
        this.areaResourceIdChange(action.pathname, action.query);
        this.observer.observe(this.lazyLoadRef.current);
        this.scrollBottomObserver.observe(this.lazyLoadRef.current);
        return;
      case 'hamburgerSlugSwitch':
      case 'searchSlugChanged':
        // Both slug-change paths use handleClientSideLoadedSrp which fetches
        // area, filter, listings, and nearby listings in sequence. Pass empty
        // query so the slug alone determines the filter via searchSlugToFilter.
        dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', false));
        this.handleClientSideLoadedSrp(action.pathname, {}).then(() => {
          emitTrackViewSrpEvent();
        });
        return;
      case 'pageChanged':
        this.queryParamaterChange(action.query);
        return;
      case 'mapCoordsChanged':
        this.mapChange(action.query);
        return;
      case 'none':
        return;
    }
  }

  /**
   * Disconnect Intersection Observer when the component unmounts.
   */
  componentWillUnmount() {
    this.observer.disconnect();
    this.scrollBottomObserver.disconnect();
    if (this._scrollRestoreCleanup) {
      this._scrollRestoreCleanup();
    }
  }

  /**
   *
   * @param {Object} reactContext - SSR context that contains `store`, `trace`, `state`, `ip`
   * Server route will mount is charged with handling all SSR related API fetching
   */
  serverRouteWillMount(reactContext) {
    const { store } = reactContext;
    const state = store.getState();
    const pathname = state.location.current.pathname;
    const { resourceId, slug } = listingUtils_getResourceIdAndSlug(pathname);
    const isNearMeUrl = routeUtils.isNearMeUrl(slug);

    const trace = reactContext.trace;
    const beforeFetchListings = [];

    const fetchAsyncAreaData = (areaResourceId) => {
      // Promises executed in parallel
      beforeFetchListings.push(
        // Fetch filter via search slug
        srwmFetchFilterFromUrlAndApi(reactContext),
        // Fecth area data
        srwmFetchAreaDataByResourceId(reactContext, { areaResourceId }),
      );

      return Promise.all(beforeFetchListings)
        .then((responses) => {
          // Check if there are any errors in the responses i.e. invalid slug or areas
          for (const res of responses) {
            if (res && (res.error || res.changeHttpStatus)) {
              return res;
            }
          }

          return Promise.all(srwmFetchListingsAndOtherData(reactContext));
        })
        .then(() => {
          return srwmCheckAndFetchNearbyListings(reactContext);
        })
        .catch((err) => {
          logError({ error: err, context: 'error in SearchResultsPage#serverRouteWillMount' });
        });
    };

    if (isNearMeUrl) {
      trace.startAsyncSpan({ marker: 'FetchAreaWrapper#loadGeolocationData' });
      return loadGeolocation(reactContext).then((geoData) => {
        // IP address might be out of range for `area/byIp` endpoint
        if (geoData === false) {
          logError({
            error: 'FetchAreaWrapper.serverRouteWillMount.geoPromise',
            errorType: 'SearchResultsPageError',
            context: `No geocode for IP ${reactContext.ip}`,
          });
          // Geo data can't be pulled from IP... use default area
          return fetchAsyncAreaData('new-york-ny');
        }
        const geoDataId = geoData.area && geoData.area.resourceId;

        return fetchAsyncAreaData(geoDataId);
      });
    }

    return fetchAsyncAreaData(resourceId);
  }

  /**
   * SRP page has been client side loaded:
   * - Via search bar menu on homepage
   * - Via "apartments-for-rent" or "homes-for-rent" on ham menu
   *
   * Fetch listings!
   */
  handleClientSideLoadedSrp = async (pathname, query) => {
    const { dispatch } = this.props;
    const { resourceId } = listingUtils_getResourceIdAndSlug(pathname);

    try {
      await dispatch(csrFetchAreaDataByResourceId(resourceId));
      await dispatch(csrFetchFilterListingsAndSyncRecentSearch(pathname, query));
      await dispatch(csrCheckAndFetchNearbyListings());
    } catch (err) {
      logError({ error: err, context: 'SearchResultsPage#handleClientSideLoadedSrp' });
      this._lastFetchFailed = true;
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', true));
    }
  };

  // HP-7394: Re-fetch listings without re-fetching the filter. Used when
  // returning from HDP on mobile where the filter state is already correct
  // (preserved by the isPadOrBuildingUrl guard in FilterActions).
  handleListingsOnlyRefresh = async (pathname, query) => {
    const { dispatch } = this.props;
    const { resourceId } = listingUtils_getResourceIdAndSlug(pathname);

    try {
      await dispatch(csrFetchAreaDataByResourceId(resourceId));
      await dispatch(csrFetchListingsAndSyncRecentSearch(pathname, query));
      await dispatch(csrCheckAndFetchNearbyListings());
    } catch (err) {
      logError({ error: err, context: 'SearchResultsPage#handleListingsOnlyRefresh' });
      this._lastFetchFailed = true;
      dispatch(AppActions.setAppStoreBool('fetchListingsByCoordsComplete', true));
    }
  };

  /**
   *
   * @param {string} pathname - the url path in the user's browser
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches all area and listings data for client side changes when a user
   * navigates to completely different area - WORK IN PROGRESS...
   */
  areaResourceIdChange = (pathname, query) => {
    const { dispatch } = this.props;
    const isNearMeUrl = routeUtils.isNearMeUrl(pathname);
    const { resourceId } = listingUtils_getResourceIdAndSlug(pathname);
    // Can be used for client side rendering
    // Search slug to filter
    // Area info by resource id

    const fetchAsyncClientSideAreaData = (id) => {
      dispatch(csrFetchAreaDataByResourceId(id))
        .then(async (res) => {
          if (res.success) {
            await dispatch(csrFetchFilterListingsAndSyncRecentSearch(pathname, query));
            emitTrackViewSrpEvent();
            return { success: true };
          }
        })
        .then(() => {
          dispatch(csrCheckAndFetchNearbyListings());
          return { success: true };
        })
        .catch((err) => {
          logError({ error: err, context: 'error in SearchResultsPage#fetchAreaData' });
        });
    };

    if (isNearMeUrl) {
      dispatch(csrFetchGeoLocation()).then((geoData) => {
        // IP address might be out of range for `area/byIp` endpoint
        if (geoData === false) {
          // Geo data can't be pulled from IP... use default area
          return fetchAsyncClientSideAreaData('new-york-ny');
        }
        const geoDataId = geoData.area && geoData.area.resourceId;

        return fetchAsyncClientSideAreaData(geoDataId);
      });
    } else {
      return fetchAsyncClientSideAreaData(resourceId);
    }
  };

  /**
   *
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches listings data for client side changes when a user triggers a
   * change in query params (page, filters, etc) - WORK IN PROGRESS
   */
  queryParamaterChange = (query) => {
    const { dispatch } = this.props;
    dispatch(csrFetchListingsOnly(query)).then(() => {
      emitTrackViewSrpEvent();
    });
  };

  /**
   *
   * @param {Object} query - the url query parameters in the user's browser
   *
   * Fetches new listing data and price filter histogram data when the user
   * moves the map around
   */
  mapChange = (query) => {
    const { dispatch } = this.props;
    dispatch(csrFetchListingsAndHistogramData(query));
  };

  syncRecentSearchIfStale = () => {
    const { dispatch, filter, pathname, query, recentSearch } = this.props;
    const shouldSync = shouldSyncRecentSearchOnSrpMount({
      pathname,
      query,
      recentSearch,
    });

    if (!shouldSync) {
      this.recentSearchSyncKey = null;
      return;
    }

    const syncKey = JSON.stringify({
      pathname,
      query,
      recentSearchUrl: recentSearch?.url,
      searchSlug: filter?.search?.slug,
    });

    if (this.recentSearchSyncKey === syncKey) {
      return;
    }

    this.recentSearchSyncKey = syncKey;
    dispatch(UserSearchActions.updateRecentSearch());
  };

  /**
   * Below the fold content; these API calls are lazily made
   */
  fetchBelowTheFoldContent = () => {
    const { dispatch, areaResourceId, isInitialSsrpPage, searchSlug } = this.props;

    // Lazy load facts if the page is CSR unless it's houses-for-rent
    if (searchSlug !== 'houses-for-rent') {
      // Area facts
      dispatch(AreaActions.loadFacts(areaResourceId));
      dispatch(AreaActions.loadFactsByBeds(areaResourceId));
    }

    // Lazy load articles if the page is CSR; articles will always be fetched on SSR
    if (!isInitialSsrpPage) {
      // Area Articles
      dispatch(AreaActions.loadArticles(areaResourceId));
    }
  };

  /**
   * Triggers initialization of Google maps when
   * 1) All filters button is clicked
   * 2) The Map button is clicked
   */
  onHandleInitializeMapClick = yieldCallback(() => {
    this.setState({
      isMapInitialized: true,
    });
  });

  render() {
    const { listings, isValidAreaPage, isValidSearchSlug } = this.props;
    const shouldRenderNotFoundPage = !isValidAreaPage || !isValidSearchSlug;

    if (shouldRenderNotFoundPage) {
      return <NotFoundPage />;
    }

    return (
      <>
        <AreaHead />
        <S.header>
          <FilterBar />
        </S.header>
        <S.main>
          <MapButton
            isMapInitialized={this.state.isMapInitialized}
            handleInitializeMapClick={this.onHandleInitializeMapClick}
          />
          <MobileListingView
            items={listings}
            lazyLoadRef={this.lazyLoadRef}
            handleInitializeMapClick={this.onHandleInitializeMapClick}
            showHaloFeed={this.props.showHaloFeed}
            listingsLoading={!this.props.fetchListingsByCoordsComplete}
          />
          <S.SemanticDivider />
          <SafeDeferredRender mode={RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_IMMEDIATE}>
            <AreaInformationWrapper />
            <NextPageLink />
            <SeoFooter />
          </SafeDeferredRender>
        </S.main>
        <Footer />
        {this.props.isMobile && this.props.showHaloShortlist && (
          <SavedListingsProvider>
            <MobileShortlistBottomSheet />
          </SavedListingsProvider>
        )}
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    pathname: ownProps.location.pathname,
    query: queryUtils.parse(ownProps.location.search),
    filter: state.filter,
    recentSearch: state.user.search.recent,
    areaResourceId: state.area.area.resourceId,
    listings: state.listings.listingGroups.byCoords,
    isValidAreaPage: state.app.isValidAreaPage,
    isValidSearchSlug: state.app.isValidSearchSlug,
    isInitialSsrpPage: state.app.isInitialSsrPage,
    isClientSideLoadedPage: state.app.isClientSideLoadedPage,
    isHamburgerMenuOpen: state.app.hamburger,
    searchSlug: state.filter.search.slug,
    showHaloFeed: selectShowHaloFeed(state),
    showHaloShortlist: selectShowHaloShortlist(state),
    fetchListingsByCoordsComplete: state.app.fetchListingsByCoordsComplete,
    isMobile: state.app.device.screenWidth === 'sm',
  };
};

export default connect(mapStateToProps)(SearchResultsPage);
